import { TEAM_COLORS, TEAM_ICONS } from '@/components/teams/TeamEditDialog';
import type { TemplateDefaultTeam } from '@/config/objects/templates';
import { cn } from '@/lib/utils';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FormFieldGroup,
  ImageUploadDropzone,
  Input,
  Label,
  Switch,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { useState } from 'react';

const DEFAULT_ICON_NAME = 'users';
const DEFAULT_ICON_COLOR = '#14B8A6';

export interface DefaultTeamDialogProps {
  templateId: string;
  /** Present when editing; omit (or null) to create a new default team. */
  team?: TemplateDefaultTeam | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Create/edit dialog for a template's DEFAULT teams — the roster seeded into
 * every event instantiated from the template (join codes are generated fresh
 * per event, so none are shown here). Reuses the event team icon/color
 * pickers so template teams render identically once instantiated.
 */
export function DefaultTeamDialog({
  templateId,
  team,
  open,
  onOpenChange,
}: DefaultTeamDialogProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const isEdit = !!team;

  const [name, setName] = useState(team?.name ?? '');
  const [emoji, setEmoji] = useState(team?.emoji ?? '');
  const [iconName, setIconName] = useState(team?.iconName || DEFAULT_ICON_NAME);
  const [iconColor, setIconColor] = useState(team?.iconColor || DEFAULT_ICON_COLOR);
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(team?.photoUrl ?? undefined);
  const [isStaff, setIsStaff] = useState<boolean>(team?.isStaff ?? false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        emoji: emoji.trim() || null,
        iconName,
        iconColor,
        // `null` clears the photo back to the icon fallback.
        photoUrl: photoUrl ?? null,
        isStaff,
      };
      return isEdit
        ? apiClient.patch(`/templates/${templateId}/teams/${team.id}`, payload)
        : apiClient.post(`/templates/${templateId}/teams`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TemplateTeams', templateId] });
      toast.success(isEdit ? 'Default team updated' : 'Default team created');
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(
        err?.message || (isEdit ? 'Failed to update default team' : 'Failed to create default team'),
      );
    },
  });

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) {
      errs.name = 'Team name is required';
    } else if (name.trim().length > 100) {
      errs.name = 'Team name must be 100 characters or less';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    saveMutation.mutate();
  };

  const CurrentIcon = TEAM_ICONS.find((i) => i.name === iconName)?.icon || Users;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Default Team' : 'Add Default Team'}</DialogTitle>
          <DialogDescription>
            Seeded into every event created from this template, with a fresh join code per event.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-4">
          {/* Preview — photo wins when set */}
          <div className="flex items-center justify-center">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={`${name || 'Team'} team photo`}
                className="h-16 w-16 rounded-full object-cover"
              />
            ) : (
              <div
                className="flex items-center justify-center h-16 w-16 rounded-full"
                style={{ backgroundColor: `${iconColor}20` }}
              >
                <CurrentIcon className="h-8 w-8" style={{ color: iconColor }} />
              </div>
            )}
          </div>

          {/* Team photo upload — overrides the icon when set */}
          <ImageUploadDropzone
            label="Team photo (optional)"
            value={photoUrl}
            onChange={(url) => setPhotoUrl(url)}
            targetAspectRatio={1}
            maxSizeMB={5}
          />

          <FormFieldGroup name="name" label="Team Name" required error={errors.name}>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter team name"
              className={cn(errors.name && 'border-destructive')}
            />
          </FormFieldGroup>

          <FormFieldGroup name="emoji" label="Team Emoji" description="Optional emoji for the team">
            <Input
              id="emoji"
              value={emoji}
              onChange={(e) => setEmoji(e.target.value)}
              placeholder="e.g., 🦊"
              maxLength={10}
            />
          </FormFieldGroup>

          {/* Icon Picker */}
          <div className="space-y-2">
            <Label>Icon</Label>
            <div className="grid grid-cols-6 gap-2">
              {TEAM_ICONS.map((item) => {
                const Icon = item.icon;
                const isSelected = item.name === iconName;
                return (
                  <button
                    key={item.name}
                    type="button"
                    aria-label={`Icon ${item.name}`}
                    onClick={() => setIconName(item.name)}
                    className={cn(
                      'flex items-center justify-center h-10 w-10 rounded-lg border transition-all',
                      isSelected
                        ? 'border-primary bg-primary/10 ring-2 ring-primary ring-offset-1'
                        : 'border-muted hover:border-primary/50 hover:bg-muted/50',
                    )}
                  >
                    <Icon
                      className="h-5 w-5"
                      style={{ color: isSelected ? iconColor : undefined }}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color Picker */}
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {TEAM_COLORS.map((color) => {
                const isSelected = color.value === iconColor;
                return (
                  <button
                    key={color.value}
                    type="button"
                    aria-label={`Color ${color.name}`}
                    onClick={() => setIconColor(color.value)}
                    className={cn(
                      'h-8 w-8 rounded-full transition-all',
                      isSelected && 'ring-2 ring-offset-2 ring-primary',
                    )}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                );
              })}
            </div>
          </div>

          {/* Staff team toggle */}
          <FormFieldGroup
            name="isStaff"
            label="Staff team"
            description="In events created from this template, this team is excluded from the leaderboard and from speed-bonus / max-completion counts."
          >
            <div
              role="button"
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => setIsStaff((v) => !v)}
            >
              <Switch checked={isStaff} tabIndex={-1} />
              <span className="text-sm font-medium">
                {isStaff ? 'Staff — non-competing' : 'Regular team'}
              </span>
            </div>
          </FormFieldGroup>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={saveMutation.isPending}>
            {isEdit ? 'Save Changes' : 'Create Team'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
